// One renderer for a CHANNEL_FIELDS descriptor, shared by every surface that
// edits creative copy: the create wizard, the campaign wizard, and the library
// drawer. Extracted from MetaCreativeEditor so the drawer stops rendering wire
// columns (`spec` as raw JSON in a text box) and shows the same fields the
// creative was built with — for CTV and Display too, whose descriptors already
// existed but had never been rendered anywhere.
//
// Descriptor vocabulary (see screens/creative-config.tsx): key, label, type
// ('input' | 'textarea' | 'select'), options, help, max, rows, mono.

import { useId } from 'react';
import { SearchableDropdown } from '@/components/dropdowns';
import { selectOptionsFor, type CreativeField } from './creative-config';

/** Fields that must not be blank. There is no `required` flag on the
 *  descriptors, so requiredness is keyed off these names.
 *
 *  `post_text`, `headline` and `cta` are required by the API
 *  (`MetaCreativeSpec::validate`) but were absent here, so clearing a prefilled
 *  headline produced a server 422 with no field to point at. */
export const REQUIRED_FIELD_KEYS = new Set([
  'dest_url',
  'display_url',
  'post_text',
  'headline',
  'cta',
]);

export interface CreativeFieldRowProps {
  field: CreativeField;
  value: string | null | undefined;
  onChange: (key: string, value: string) => void;
  /** A short note beside the label (the wizard's "from the campaign" hint). */
  hint?: string | null;
  disabled?: boolean;
}

export function CreativeFieldRow({ field, value, onChange, hint, disabled = false }: CreativeFieldRowProps) {
  const inputId = useId();
  const v = value ?? '';
  const required = REQUIRED_FIELD_KEYS.has(field.key);
  return (
    <div className="field">
      <div className="flex between items-baseline" style={{marginBottom:4}}>
        <label htmlFor={inputId} className="field-lbl" style={{margin:0}}>
          {field.label}
          {required && <span style={{color:'var(--live)'}}> *</span>}
          {field.help && (
            <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:8,fontSize:10.5}}>
              {field.help}
            </span>
          )}
          {hint && (
            <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:8,fontSize:10.5,color:'var(--accent)'}}>
              · {hint}
            </span>
          )}
        </label>
        {/* `field.max` is Meta's recommended length, not a limit — going past it
            costs a "See more" clamp, not a rejection. Warn, never block. The
            hard stop is `field.hardMax`, which is what the API enforces. */}
        {field.max && (
          <span
            className="muted mono"
            style={{fontSize:10, color: v.length > field.max ? 'var(--warn)' : undefined}}
            title={v.length > field.max ? `Over Meta's recommended ${field.max} characters — this still runs, but may be truncated in feed.` : undefined}
          >
            {v.length}/{field.max}
          </span>
        )}
      </div>

      {field.type === 'textarea' && (
        <textarea
          id={inputId}
          className="input"
          rows={field.rows || 2}
          maxLength={field.hardMax}
          value={v}
          disabled={disabled}
          onChange={event => onChange(field.key, event.target.value)}
        />
      )}
      {field.type === 'input' && (
        <input
          id={inputId}
          data-validation-focus={required ? true : undefined}
          type={field.key === 'dest_url' ? 'url' : 'text'}
          className={`input ${field.mono ? 'mono' : ''}`}
          style={field.mono ? {fontSize:11.5} : {}}
          maxLength={field.hardMax}
          value={v}
          disabled={disabled}
          onChange={event => onChange(field.key, event.target.value)}
        />
      )}
      {field.type === 'select' && (
        <SearchableDropdown
          ariaLabel={field.label}
          value={v}
          options={selectOptionsFor(field, v).map(option => ({ id: option, label: option }))}
          disabled={disabled}
          onChange={next => onChange(field.key, next)}
          searchPlaceholder={`Search ${field.label.toLowerCase()}…`}
        />
      )}
    </div>
  );
}
